// Event wizard step — event details + tiers editor (SLICE-EVENT-TICKET-PASS).
//
// Edits the whole EventTicketConfig (type_config). The owner sets the event
// name / date / venue / capacity and a list of tiers (GA / VIP / …), each with
// an optional price + per-tier cap + perks. The whole config is patched on
// every change (the API expects the WHOLE EventTicketConfig).

import {
  buildDefaultEventTicketDraft,
  newId,
  EVENT_MAX_INCLUSIONS,
  EVENT_MAX_TIERS,
  EVENT_NAME_MAX_LENGTH,
  EVENT_TIER_NAME_MAX_LENGTH,
  EVENT_VENUE_ADDRESS_MAX_LENGTH,
  EVENT_VENUE_NAME_MAX_LENGTH,
  type EventTicketConfig,
  type EventTier,
} from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'

const EMPTY_EVENT_CONFIG: EventTicketConfig =
  buildDefaultEventTicketDraft().type_config

const pad = (n: number) => String(n).padStart(2, '0')

// The owner types PH local time in the datetime-local input; we store UTC ISO
// (the wallet builders render it back in Asia/Manila). PH is UTC+8, no DST.
function isoToLocalInput(iso: string): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const p = new Date(t + 8 * 3600_000)
  return `${p.getUTCFullYear()}-${pad(p.getUTCMonth() + 1)}-${pad(
    p.getUTCDate(),
  )}T${pad(p.getUTCHours())}:${pad(p.getUTCMinutes())}`
}
function localInputToIso(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return ''
  const [, y, mo, d, h, mi] = m
  const utc = Date.UTC(+y!, +mo! - 1, +d!, +h!, +mi!) - 8 * 3600_000
  return new Date(utc).toISOString()
}

const tierRowStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
  padding: 10,
  border: '1px solid var(--owner-border, #e2e2e2)',
  borderRadius: 8,
  marginBottom: 8,
}

export function StepEvent({ builder }: { builder: BuilderController }) {
  const config: EventTicketConfig = {
    ...EMPTY_EVENT_CONFIG,
    ...((builder.draft.type_config as EventTicketConfig | null) ?? {}),
  }

  function patch(next: Partial<EventTicketConfig>) {
    builder.update({ type_config: { ...config, ...next } })
  }

  const tiers = config.tiers
  const canAddTier = tiers.length < EVENT_MAX_TIERS
  function setTiers(next: EventTier[]) {
    patch({ tiers: next })
  }
  function updateTier(i: number, partial: Partial<EventTier>) {
    setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...partial } : t)))
  }
  function removeTier(i: number) {
    setTiers(tiers.filter((_, idx) => idx !== i))
  }
  function addTier() {
    if (!canAddTier) return
    setTiers([
      ...tiers,
      { id: newId(), name: '', price_centavos: null, inclusions: [], capacity: null },
    ])
  }

  return (
    <div className="program-step">
      <h2>Event details</h2>
      <p className="program-step-sub">
        The event name, when and where it happens, and the ticket tiers people
        can get. Each ticket is issued against one tier.
      </p>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-name">
          Event name
        </label>
        <input
          id="event-name"
          type="text"
          className="owner-input"
          value={config.event_name}
          maxLength={EVENT_NAME_MAX_LENGTH}
          placeholder="e.g. Launch Night"
          onChange={(e) => patch({ event_name: e.target.value })}
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-start">
          Starts
        </label>
        <input
          id="event-start"
          type="datetime-local"
          className="owner-input"
          value={isoToLocalInput(config.starts_at)}
          onChange={(e) => patch({ starts_at: localInputToIso(e.target.value) })}
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-end">
          Ends <span className="program-field-hint">(optional)</span>
        </label>
        <input
          id="event-end"
          type="datetime-local"
          className="owner-input"
          value={isoToLocalInput(config.ends_at ?? '')}
          onChange={(e) =>
            patch({
              ends_at: e.target.value
                ? localInputToIso(e.target.value)
                : undefined,
            })
          }
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-venue">
          Venue name
        </label>
        <input
          id="event-venue"
          type="text"
          className="owner-input"
          value={config.venue_name}
          maxLength={EVENT_VENUE_NAME_MAX_LENGTH}
          placeholder="e.g. Gatchaa Main"
          onChange={(e) => patch({ venue_name: e.target.value })}
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-address">
          Venue address <span className="program-field-hint">(optional)</span>
        </label>
        <input
          id="event-address"
          type="text"
          className="owner-input"
          value={config.venue_address ?? ''}
          maxLength={EVENT_VENUE_ADDRESS_MAX_LENGTH}
          placeholder="e.g. Rizal Ave, Puerto Princesa"
          onChange={(e) =>
            patch({ venue_address: e.target.value || undefined })
          }
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="event-capacity">
          Total capacity <span className="program-field-hint">(optional)</span>
        </label>
        <span className="program-field-hint">
          The most tickets you'll issue across all tiers. Leave blank for no
          limit.
        </span>
        <input
          id="event-capacity"
          type="number"
          min={1}
          className="owner-input"
          value={config.capacity ?? ''}
          placeholder="No limit"
          onChange={(e) =>
            patch({
              capacity: e.target.value
                ? Math.max(1, parseInt(e.target.value, 10) || 1)
                : null,
            })
          }
        />
      </div>

      <div className="program-field-block">
        <label className="program-toggle">
          <input
            type="checkbox"
            checked={config.holder_name_required}
            onChange={(e) => patch({ holder_name_required: e.target.checked })}
          />
          <span className="program-field-label">Collect the attendee's name</span>
        </label>
        <span className="program-field-hint">
          Ask for a name when the ticket is issued or claimed (printed on the
          ticket).
        </span>
      </div>

      <div className="program-field-block">
        <span className="program-field-label">Ticket tiers</span>
        <span className="program-field-hint">
          e.g. General Admission, VIP, Early Bird. Price and perks are optional.
        </span>
        <div>
          {tiers.map((tier, i) => (
            <div style={tierRowStyle} key={tier.id}>
              <input
                type="text"
                className="owner-input"
                value={tier.name}
                maxLength={EVENT_TIER_NAME_MAX_LENGTH}
                placeholder="Tier name (e.g. VIP)"
                onChange={(e) => updateTier(i, { name: e.target.value })}
                aria-label={`Tier ${i + 1} name`}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="owner-input"
                  style={{ flex: 1 }}
                  value={tier.price_centavos != null ? tier.price_centavos / 100 : ''}
                  placeholder="Price ₱"
                  onChange={(e) =>
                    updateTier(i, {
                      price_centavos: e.target.value
                        ? Math.max(0, Math.round(parseFloat(e.target.value) * 100) || 0)
                        : null,
                    })
                  }
                  aria-label={`Tier ${i + 1} price`}
                />
                <input
                  type="number"
                  min={1}
                  className="owner-input"
                  style={{ flex: 1 }}
                  value={tier.capacity ?? ''}
                  placeholder="Cap"
                  onChange={(e) =>
                    updateTier(i, {
                      capacity: e.target.value
                        ? Math.max(1, parseInt(e.target.value, 10) || 1)
                        : null,
                    })
                  }
                  aria-label={`Tier ${i + 1} capacity`}
                />
              </div>
              <input
                type="text"
                className="owner-input"
                value={tier.inclusions.join(', ')}
                placeholder="Perks, comma-separated (optional)"
                onChange={(e) =>
                  updateTier(i, {
                    inclusions: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, EVENT_MAX_INCLUSIONS),
                  })
                }
                aria-label={`Tier ${i + 1} perks`}
              />
              {tiers.length > 1 && (
                <button
                  type="button"
                  className="program-link-btn program-danger"
                  onClick={() => removeTier(i)}
                  style={{ justifySelf: 'start' }}
                >
                  Remove tier
                </button>
              )}
            </div>
          ))}
        </div>
        {canAddTier && (
          <button
            type="button"
            className="owner-btn program-add-btn"
            onClick={addTier}
          >
            + Add tier
          </button>
        )}
      </div>
    </div>
  )
}
